/**
 * Coach Engine — Plain Narrator
 *
 * Default, offline narrator. Turns an array of Observation objects into
 * a single human-readable string for surfaces that want text (legacy
 * session aiInsight.text slot, logs, tooltips).
 *
 * Keeps the output concise — sentence per observation, joined with
 * space. Surfaces that want richer rendering should consume observations
 * directly instead of calling this.
 */

/**
 * Render one observation as a single sentence. Prefer title + detail +
 * action; fall back gracefully if any are missing.
 *
 * @param {Observation} obs
 * @returns {string}
 */
export const renderObservation = (obs) => {
  if (!obs) return '';
  const parts = [];
  if (obs.title) parts.push(obs.title);
  if (obs.detail) parts.push(obs.detail);
  if (obs.action) parts.push(obs.action);
  // Filter falsy BEFORE trim — `s.trim()` throws if s is null/undefined
  // (the upstream `if (obs.title)` guards string-truthy, but a future
  // analyzer could push a non-string accidentally). Filter+coerce first.
  return parts
    .filter(s => typeof s === 'string' && s.length > 0)
    .map(s => s.trim().replace(/\s+/g, ' '))
    .filter(Boolean)
    .join(' — ')
    // Collapse repeated em-dash separators if any field was effectively empty
    // after trim. The previous /  — — /g only caught a single doubled instance;
    // the global replace below handles arbitrary chains (e.g. " — — — ").
    .replace(/( — )+/g, ' — ');
};

/**
 * Render a list of observations as one string, joined with space.
 * Each observation becomes its own sentence.
 *
 * @param {Observation[]} observations
 * @returns {string}
 */
export const renderAll = (observations) => {
  if (!Array.isArray(observations)) return '';
  return observations
    .map(renderObservation)
    .filter(Boolean)
    .join(' ');
};

export default { renderObservation, renderAll };
